/**
 * ChatActionItem
 *
 * A single row in the chat-actions sheet: a rounded icon tile (tinted glyph on
 * a light background) followed by the action label to its right.
 *
 * Flutter source of truth:
 * `lib/domain/entity/bottom_sheet/chat_actions_bottom_sheet.dart:581-637`
 * (`actionItem`) — a circular `backgroundColorLight` tile holding a half-size
 * `disabledColor` glyph, then the label text.
 *
 * UI tokens follow web conventions (theme / responsive), per `presentation.md`.
 */
import React from 'react';
import { useTheme } from '@/presentation/contexts/ThemeContext';
import { useResponsiveDesign } from '@/presentation/responsiveDesign/responsiveDesign';
import { AssetIcon } from '@/presentation/components/common/icon/AssetIcon';
import { qaAttr } from '@/presentation/utils/qaAttr';
import type { ChatActionType } from '@/domain/enums/chat/chatActionType';

export interface ChatActionItemProps {
  /** Action type — also suffixes the row's `data-qa-id`. */
  type: ChatActionType;
  /** Icon asset path for the glyph (e.g. `/assets/images/block.png`). */
  iconPath: string;
  /** Resolved, localized label text. */
  label: string;
  /** Row tap handler. */
  onClick: () => void;
}

export const ChatActionItem: React.FC<ChatActionItemProps> = ({
  type,
  iconPath,
  label,
  onClick,
}) => {
  const { theme } = useTheme();
  const responsive = useResponsiveDesign();

  // Flutter `actionItem`: tile = `contentActionsImageSize`, glyph = half that.
  const tileSize = responsive.action.imageSize;
  const glyphSize = tileSize / 2;

  return (
    <button
      {...qaAttr({ id: `chat-action-${type}` })}
      onClick={onClick}
      style={{
        display: 'flex',
        alignItems: 'center',
        width: '100%',
        gap: responsive.spacing.sm,
        background: 'none',
        border: 'none',
        cursor: 'pointer',
        padding: 0,
        textAlign: 'left',
      }}
    >
      {/* Icon tile — rounded light background with a centered tinted glyph. */}
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          flexShrink: 0,
          width: tileSize,
          height: tileSize,
          borderRadius: responsive.borderRadius.round,
          backgroundColor: theme.surface,
        }}
      >
        <AssetIcon
          path={iconPath}
          width={glyphSize}
          height={glyphSize}
          color={theme.disabled}
        />
      </div>

      {/* Label */}
      <span
        style={{
          minWidth: 0,
          flex: 1,
          color: theme.text.primary,
          fontSize: responsive.font.base,
          fontWeight: responsive.fontWeight.medium,
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
        }}
      >
        {label}
      </span>
    </button>
  );
};
